// frontend/src/context/AcademicYearContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getAllAcademicYears, setCurrentAcademicYear } from '../services/academicYearService';
import { message } from 'antd';
import { useAuth } from './AuthContext';

const AcademicYearContext = createContext(null);

export const AcademicYearProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [currentYear, setCurrentYear] = useState(null);
    const [allYears, setAllYears] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchYears = useCallback(async () => {
        if (!isAuthenticated) return;
        setLoading(true);
        try {
            const years = await getAllAcademicYears();
            setAllYears(years);
            const current = years.find(y => y.is_current);
            if (current) {
                setCurrentYear(current);
            } else {
                // Warning: No current year is set
                // We don't block the UI, but we set currentYear to null explicitly
                // Components should check for currentYear existence before rendering data
                console.warn('No active academic year found. Please configure one in Settings.');
                setCurrentYear(null); 
            }
        } catch (error) {
            console.error('Failed to fetch academic year data:', error);
            message.error('Failed to load academic years.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchYears();
        } else {
            // If not authenticated, set a default state so the loading indicator goes away
            setCurrentYear(null);
            setAllYears([]);
            setLoading(false);
        }
    }, [isAuthenticated, fetchYears]);

    const changeCurrentYear = async (yearId) => {
        try {
            const response = await setCurrentAcademicYear(yearId);
            message.success(response.message);
            fetchYears(); // Re-fetch data to update all components
        } catch (error) {
            message.error('Failed to change current academic year.');
        }
    };

    return (
        <AcademicYearContext.Provider value={{ currentYear, allYears, changeCurrentYear, loading }}>
            {children}
        </AcademicYearContext.Provider>
    );
};

export const useAcademicYear = () => useContext(AcademicYearContext);